import { Pause, Play } from "lucide-react"
import { type MouseEvent, type ReactNode, useRef, useState } from "react"

// Extracted from the lead-responder drawer so the conversation drawer plays a
// recording the same way. Behaviour is unchanged from that original.

// "3m 12s" → seconds. Used as the total until the audio's own metadata loads.
export function parseDuration(d: string) {
  const m = /(\d+)\s*m/.exec(d)
  const s = /(\d+)\s*s/.exec(d)
  return (m ? Number(m[1]) * 60 : 0) + (s ? Number(s[1]) : 0)
}

// seconds → "m:ss"
export function formatClock(sec: number) {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

// Recording player wired to the real call recording: play/pause, a seekable
// track, and a live elapsed / total readout. Total comes from the audio's own
// metadata, falling back to the known duration string until it loads.
export function RecordingPlayer({
  src,
  duration,
  className,
  trailing,
}: {
  src: string
  duration?: string
  className?: string
  // Rendered inside the player, after the clock — e.g. a download control, so
  // it sits within the object rather than beside it.
  trailing?: ReactNode
}) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const trackRef = useRef<HTMLButtonElement>(null)
  const [playing, setPlaying] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [total, setTotal] = useState(duration ? parseDuration(duration) : 0)

  function toggle() {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) void audio.play()
    else audio.pause()
  }

  function seek(e: MouseEvent<HTMLButtonElement>) {
    const audio = audioRef.current
    const rect = trackRef.current?.getBoundingClientRect()
    if (!audio || !rect || !total) return
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width))
    audio.currentTime = frac * total
    setElapsed(audio.currentTime)
  }

  const pct = total ? (elapsed / total) * 100 : 0

  return (
    <div
      className={
        className ??
        "bg-background flex w-full max-w-xs items-center gap-2.5 rounded-full border px-2.5 py-1.5"
      }
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration
          if (Number.isFinite(d) && d > 0) setTotal(d)
        }}
        onTimeUpdate={(e) => setElapsed(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause recording" : "Play recording"}
        className="grid size-6 shrink-0 place-items-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600"
      >
        {playing ? (
          <Pause className="size-3 fill-current" />
        ) : (
          <Play className="size-3 fill-current" />
        )}
      </button>
      <button
        type="button"
        ref={trackRef}
        onClick={seek}
        aria-label="Seek recording"
        className="bg-border relative h-1 flex-1 cursor-pointer rounded-full"
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
          style={{ width: `${pct}%` }}
        />
        <div
          className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500 shadow-sm"
          style={{ left: `${pct}%` }}
        />
      </button>
      <span className="text-muted-foreground shrink-0 text-[11px] tabular-nums">
        {formatClock(elapsed)} / {formatClock(total)}
      </span>
      {trailing}
    </div>
  )
}
